// src/ui/screens/TitleScreen.tsx — UI-01 title / home screen (D-05).
//
// The nostalgia-hero first-impression payload (07-UI-SPEC §Surface 1): the
// Mouchao farm hero art, the wordmark, a single dominant amber Play CTA, a
// `How to play` ghost button, and a 44x44 Settings gear. Full-viewport
// pre-game stage at z-index 150 (the gate / splash / CharacterSelect tier),
// mirroring the CharacterSelect full-viewport idiom.
//
// STANDALONE component — App.tsx wiring (the splash → title → CharacterSelect
// stage insert) lands in Plan 10. Three callbacks: onPlay advances to
// CharacterSelect, onHowTo opens the controls tutorial in preview mode,
// onSettings opens the SettingsPanel over the title.
//
// Hero art (D-05 — Claude's-discretion treatment): currently the shared
// `/splash-placeholder.png` (the same warm placeholder used by BootSplash /
// GateScreen). Flagged for the real-device gauntlet: swap for the authored
// Mouchao farm illustration (warm palette, reads as the place, no UI chrome
// baked in) when art is available — the contract here is layout + hierarchy,
// not the final pixels.
//
// ZERO new design tokens (07-UI-SPEC): 36px wordmark, 56px amber Play, ghost
// How-to, ghost gear all reuse the P0–P6 palette/sizes. prefers-reduced-motion
// disables the entrance (instant appear) via a root class read once at mount.
// Critical layout mirrored inline so jsdom specs assert without the stylesheet.
//
// SOURCE: 07-UI-SPEC.md §Surface 1 + §Copywriting Contract; 07-PATTERNS.md
//         §TitleScreen.tsx.

import { useRef, type JSX } from 'react';

import './title-screen.css';

export interface TitleScreenProps {
  /** Advance to CharacterSelect. */
  onPlay: () => void;
  /** Open the controls tutorial in preview mode (does not touch the seen flag). */
  onHowTo: () => void;
  /** Open the SettingsPanel layered over the title. */
  onSettings: () => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function TitleScreen({
  onPlay,
  onHowTo,
  onSettings,
}: TitleScreenProps): JSX.Element {
  // Capture once at mount — the user does not toggle the OS setting mid-session.
  const reducedMotion = useRef<boolean>(prefersReducedMotion());

  const rootClass = reducedMotion.current
    ? 'title-screen title-screen--reduced-motion'
    : 'title-screen';

  return (
    <main
      className={rootClass}
      role="main"
      data-testid="title-screen"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#0d1f2d',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        color: '#f0ead8',
      }}
    >
      <div className="title-screen__hero-wrap">
        <img
          className="title-screen__hero"
          src="/splash-placeholder.png"
          alt="The Mouchao farm"
          data-testid="title-hero"
        />
      </div>

      <h1 className="title-screen__wordmark" data-testid="title-wordmark">
        Mouchao Hunter
      </h1>

      <button
        type="button"
        className="title-screen__play"
        data-testid="title-play"
        onClick={onPlay}
      >
        Play
      </button>

      <div className="title-screen__row">
        <button
          type="button"
          className="title-screen__howto"
          data-testid="title-howto"
          onClick={onHowTo}
        >
          How to play
        </button>

        <button
          type="button"
          className="title-screen__gear"
          data-testid="title-settings"
          aria-label="Settings"
          onClick={onSettings}
        >
          {/* Inline SVG gear pictograph — no external icon lib (project pattern). */}
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="none"
              stroke="#f0ead8"
              strokeWidth="2"
              strokeLinejoin="round"
              d="M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zM12 2.5l1.3 2.1 2.4-.5.6 2.4 2.2 1.1-.6 2.4 1.6 1.9-1.6 1.9.6 2.4-2.2 1.1-.6 2.4-2.4-.5L12 21.5l-1.3-2.1-2.4.5-.6-2.4-2.2-1.1.6-2.4L3.1 12l1.6-1.9-.6-2.4 2.2-1.1.6-2.4 2.4.5L12 2.5z"
            />
          </svg>
        </button>
      </div>
    </main>
  );
}
